import React, { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"

import * as Commands from "../../../../../model/commands"
import { ID, WebSource } from "../../../../../model/types"
import api from "../../../../../services/api"
import IdeaBox, { FleetingIdeaBox } from "../../../../components/idea_box"
import { useNana } from "../../../../hooks/useNana"
import useFlash from "../../../../hooks/use_flash"
import { SourcesPageState } from "../../sources_page"
import { SourceNotesState } from "../../sources_page/source_view/source_notes"
import SourceStatusTag from "../../sources_page/source_status_tag"
import MoveCard, {
  CardMeta,
  CardPrimaryButton,
  CardSecondaryButton,
  previewFromHtml,
  sourceDisplayTitle,
} from "../move_card"

export default function ExtractCard({
  sourceId,
  onReroll,
  onObjectGone,
}: {
  sourceId: ID
  onReroll: () => void
  onObjectGone: () => void
}) {
  const { model, commander } = useNana()
  const navigate = useNavigate()
  const { setFlash } = useFlash()
  const [rescanning, setRescanning] = useState(false)
  const source = model.getSource(sourceId)
  const preview = source
    ? previewFromHtml(source.literatureNotes, 640)
    : ""

  useEffect(() => {
    if (!source || !preview) onObjectGone()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, preview])

  if (!source || !preview) return null

  const open = () => {
    SourcesPageState.openSourceId = source.id
    SourceNotesState.sourceTabSelected = "permanent-notes"
    navigate("/app/sources")
  }

  const markRead = () => {
    commander.exec(
      new Commands.SetWebSourceFieldCommand({
        id: source.id,
        field: "status",
        value: "read",
      }),
    )
    onObjectGone()
  }

  const rescanMetadata = async () => {
    if (rescanning) return
    setRescanning(true)
    try {
      await api().post(`/api/sources/${source.id}/metadata/reprocess`)
      setFlash({
        type: "success",
        text: "Metadata rescan queued",
      })
    } catch {
      setFlash({
        type: "error",
        text: "Could not queue metadata rescan",
      })
    } finally {
      setRescanning(false)
    }
  }

  const title = sourceDisplayTitle(source)
  const fleeting = model.getFleetingIdeasFromSource(source.id)
  const ideas = model.getIdeasFromSource(source.id)

  return (
    <MoveCard
      move="extract"
      onReroll={onReroll}
      onPrimary={open}
      actions={
        <>
          <CardPrimaryButton onClick={open}>Extract ideas</CardPrimaryButton>
          <CardSecondaryButton onClick={rescanMetadata}>
            {rescanning ? "Rescanning…" : "Rescan metadata"}
          </CardSecondaryButton>
          <CardSecondaryButton onClick={markRead}>Mark read</CardSecondaryButton>
        </>
      }
    >
      <div className="flex min-h-[7.5rem] flex-1 flex-col gap-3">
        <h3 className="line-clamp-2 break-words text-[1.15rem] font-semibold leading-snug tracking-[-0.02em] text-stone-900 dark:text-white">
          {title}
        </h3>

        <div className="rounded-md bg-amber-50 px-3.5 py-3 ring-1 ring-inset ring-amber-900/10 dark:bg-amber-950/40 dark:ring-amber-100/10">
          <p className="line-clamp-5 text-[14px] leading-[1.55] text-stone-800 dark:text-amber-50/90">
            {preview}
          </p>
        </div>

        {(fleeting.length > 0 || ideas.length > 0) && (
          <div className="flex items-center gap-1.5 overflow-hidden">
            {fleeting.slice(0, 3).map((f) => (
              <FleetingIdeaBox key={String(f.id)} />
            ))}
            {ideas.slice(0, 5).map((idea) => (
              <IdeaBox key={String(idea.id)} idea={idea} shadow={false} />
            ))}
          </div>
        )}

        <CardMeta>
          <SourceStatusTag source={source as WebSource} />
          <span className="opacity-70">from literature notes</span>
        </CardMeta>
      </div>
    </MoveCard>
  )
}
